/**
 * Category Repository — CRUD untuk tabel category_cache.
 *
 * Kategori adalah taksonomi aktivitas per-workspace (mis. Rapat, Dinas Luar,
 * Dokumen). Setiap workspace memiliki set kategorinya sendiri.
 *
 * Setiap operasi write menandai sync_status = 'pending' agar sync engine
 * (S14) tahu data perlu dikirim ke cloud.
 *
 * Acuan: docs/04-Database.md §6 (category_cache), docs/01-SRS.md FR-ACT-01.
 */

import type { SqlValue } from 'sql.js';
import { BaseRepository } from '../repository';
import type { CategoryCache, SyncStatus } from '../types';

/** Input untuk membuat kategori baru. */
export interface CreateCategoryInput {
  workspace_id: string;
  tenant_id: string;
  name: string;
  color?: string;
  icon?: string | null;
  sort_order?: number;
}

/** Input untuk update kategori. */
export interface UpdateCategoryInput {
  name?: string;
  color?: string;
  icon?: string | null;
  sort_order?: number;
}

export class CategoryRepository extends BaseRepository<CategoryCache> {
  protected readonly tableName = 'category_cache';

  protected mapRow(row: Record<string, unknown>): CategoryCache {
    return row as unknown as CategoryCache;
  }

  // -------------------------------------------------------------------------
  // Create
  // -------------------------------------------------------------------------

  async create(data: CreateCategoryInput): Promise<CategoryCache> {
    const id = this.generateId();
    const now = this.now();

    this.insertRow({
      id,
      workspace_id: data.workspace_id,
      tenant_id: data.tenant_id,
      name: data.name,
      color: data.color ?? '#8B6F47',
      icon: data.icon ?? null,
      sort_order: data.sort_order ?? 0,
      sync_status: 'pending',
      last_synced_at: null,
      updated_at: now,
    });

    const created = await this.findById(id);
    if (!created) throw new Error(`Failed to create category: ${id}`);
    return created;
  }

  // -------------------------------------------------------------------------
  // Update
  // -------------------------------------------------------------------------

  async update(id: string, changes: UpdateCategoryInput): Promise<CategoryCache | null> {
    const updateData: Record<string, SqlValue> = {
      updated_at: this.now(),
      sync_status: 'pending',
    };

    if (changes.name !== undefined) updateData.name = changes.name;
    if (changes.color !== undefined) updateData.color = changes.color;
    if (changes.icon !== undefined) updateData.icon = changes.icon;
    if (changes.sort_order !== undefined) updateData.sort_order = changes.sort_order;

    this.updateRow(id, updateData);
    return this.findById(id);
  }

  // -------------------------------------------------------------------------
  // Specialized Queries
  // -------------------------------------------------------------------------

  /**
   * Ambil semua kategori dalam workspace, urut berdasarkan sort_order
   * lalu nama (alphabetical fallback).
   */
  async findByWorkspace(workspaceId: string): Promise<CategoryCache[]> {
    const sql = `
      SELECT * FROM ${this.tableName}
      WHERE workspace_id = ?
      ORDER BY sort_order ASC, name ASC
    `;
    const rows = this.db.queryAll<Record<string, unknown>>(sql, [workspaceId]);
    return rows.map((row) => this.mapRow(row));
  }

  /** Cari kategori berdasarkan nama dalam workspace (case-insensitive). */
  async findByName(workspaceId: string, name: string): Promise<CategoryCache | null> {
    const sql = `
      SELECT * FROM ${this.tableName}
      WHERE workspace_id = ? AND LOWER(name) = LOWER(?)
      LIMIT 1
    `;
    const row = this.db.queryOne<Record<string, unknown>>(sql, [workspaceId, name]);
    return row ? this.mapRow(row) : null;
  }

  /** Ambil kategori dengan sync_status tertentu (untuk sync engine). */
  async findBySyncStatus(status: SyncStatus): Promise<CategoryCache[]> {
    const sql = `SELECT * FROM ${this.tableName} WHERE sync_status = ?`;
    const rows = this.db.queryAll<Record<string, unknown>>(sql, [status]);
    return rows.map((row) => this.mapRow(row));
  }

  /** Tandai kategori sebagai tersinkron. */
  async markSynced(id: string): Promise<void> {
    this.updateRow(id, {
      sync_status: 'synced',
      last_synced_at: this.now(),
    });
  }

  // -------------------------------------------------------------------------
  // Batch Operations
  // -------------------------------------------------------------------------

  /**
   * Urutkan ulang kategori dalam workspace (batch update sort_order).
   * @param workspaceId ID workspace.
   * @param orderedIds Array ID kategori dalam urutan yang diinginkan.
   */
  async reorder(workspaceId: string, orderedIds: string[]): Promise<void> {
    const now = this.now();
    orderedIds.forEach((id, index) => {
      this.updateRow(id, {
        sort_order: index,
        updated_at: now,
        sync_status: 'pending',
      });
    });
  }

  /**
   * Seed kategori default untuk workspace baru (D1).
   * Idempotent — skip jika workspace sudah punya kategori.
   */
  async seedDefaults(
    workspaceId: string,
    tenantId: string,
    defaults: { name: string; color: string; icon: string }[],
  ): Promise<CategoryCache[]> {
    // Cek apakah workspace sudah punya kategori
    const existing = await this.findByWorkspace(workspaceId);
    if (existing.length > 0) return existing;

    const created: CategoryCache[] = [];
    for (let i = 0; i < defaults.length; i++) {
      const d = defaults[i];
      const cat = await this.create({
        workspace_id: workspaceId,
        tenant_id: tenantId,
        name: d.name,
        color: d.color,
        icon: d.icon,
        sort_order: i,
      });
      created.push(cat);
    }
    return created;
  }
}
